/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.base.analytics

import co.touchlab.kermit.Logger

/**
 * Decorator around an [AnalyticsHelper] that redacts parameter values likely to
 * contain PII or PHI before they are forwarded to the underlying analytics backend.
 *
 * Cauce handles patient records. Any free-text parameter that flows into Firebase
 * Analytics or a similar third party is a potential PHI leak and a compliance
 * problem under HIPAA and LGPD. This wrapper enforces a deny list on the value
 * side of known free-text keys while preserving the event shape so that
 * dashboards still see event counts and category breakdowns.
 *
 * Redaction strategy:
 * - Event types are preserved as-is (they are developer-controlled constants).
 * - Parameter keys are preserved so dashboards remain meaningful.
 * - Parameter values for keys in [UNSAFE_VALUE_KEYS] are replaced with
 *   [REDACTED_PLACEHOLDER]. Callers that need searchable analytics on these
 *   dimensions should first hash or bucket the value themselves and pass a
 *   non-PHI token.
 * - [setUserId] drops email-shaped values (contains `@`) rather than forwarding
 *   them. The [AnalyticsHelper] contract already warns callers, but this is
 *   the single path around the sanitizer and worth enforcing at runtime.
 * - [setUserProperty] is passed through; callers are expected to keep user
 *   property values non-PII per the contract.
 */
internal class PhiSafeAnalyticsHelper(
    private val delegate: AnalyticsHelper,
) : AnalyticsHelper {

    override fun logEvent(event: AnalyticsEvent) {
        // All AnalyticsHelper overloads funnel through this single method via
        // the interface default implementations. Do not override the
        // String-based overloads — doing so would bypass sanitization if a
        // subclass ever reimplements them.
        delegate.logEvent(sanitize(event))
    }

    override fun setUserProperty(name: String, value: String) {
        delegate.setUserProperty(name, value)
    }

    override fun setUserId(userId: String) {
        if (userId.contains('@')) {
            // Log-and-drop: an `@` strongly implies an email, which is both
            // PII and a misuse of setUserId. Drop rather than throw so a
            // misbehaving caller cannot crash the app in production.
            Logger.e(TAG) { "Dropping setUserId: value looks like an email" }
            return
        }
        delegate.setUserId(userId)
    }

    private fun sanitize(event: AnalyticsEvent): AnalyticsEvent {
        if (event.extras.isEmpty()) return event
        val sanitizedExtras = event.extras.map { param ->
            if (param.key in UNSAFE_VALUE_KEYS) {
                Param(param.key, REDACTED_PLACEHOLDER)
            } else {
                param
            }
        }
        return event.copy(extras = sanitizedExtras)
    }

    internal companion object {
        private const val TAG: String = "PhiSafeAnalyticsHelper"

        const val REDACTED_PLACEHOLDER: String = "__PHI_REDACTED__"

        /**
         * Parameter keys whose values are treated as potential PHI carriers and
         * redacted on the way to analytics. Add keys here when introducing new
         * free-text dimensions rather than weakening the default.
         */
        val UNSAFE_VALUE_KEYS: Set<String> = setOf(
            ParamKeys.SEARCH_TERM,
            ParamKeys.ERROR_MESSAGE,
            ParamKeys.CONTENT_ID,
            ParamKeys.CONTENT_NAME,
            ParamKeys.FIELD_NAME,
            ParamKeys.FILTER_VALUE,
            ParamKeys.USER_ID,
        )
    }
}
